import { Injectable } from '@angular/core'
import { CurrentProductService } from '@spartacus/storefront'
import { isNotNullable, ProductScope, VariantOption, WindowRef } from '@spartacus/core'
import { classificationCodes, MbConstants } from '../../shared/mb-constants'
import { ActivatedRoute, Router } from '@angular/router'
import { FilterOptionSelection, ProductVariantFilterData } from '../../models'
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators'
import { of } from 'rxjs'

const toProductVariantFilterData = (f?: ProductVariantFilterData[], variants?: VariantOption[]) => f?.map((fd, index) => ({
  code: fd.code,
  name: fd.name,
  filterOptions: fd.filterOptions.map(fo => ({
    ...fo,
    urlName: fd.prov
      && variants?.find(v => v.variantOptionQualifiers?.find(voq => voq.qualifier === fd.code && voq.value === fo.code))?.code
      || fo.urlName.toLowerCase()
  })),
  isPrimaryFilter: index === 0,
  prov: fd.prov,
  urlName: fd.urlName?.toLowerCase()
} as ProductVariantFilterData)) || []

const hasAppliedFacet = (obj: Record<string, any>) => Object.keys(obj).find(k => !!obj[k])
const getActiveFacetValue = (v?: ProductVariantFilterData, property = 'code') => v?.filterOptions.find(fo => fo.active)?.[property]
const toFacetRecord = (pf: ProductVariantFilterData[]) =>
  pf.map(v => ({ [v.code]: getActiveFacetValue(v) })).reduce((p, n) => ({ ...p, ...n }), {})
const toHashUrl = (facets: ProductVariantFilterData[]) => facets.filter(v => getActiveFacetValue(v)).map(v =>
  v.prov && getActiveFacetValue(v, 'urlName') || `${v.urlName}${MbConstants.EQUAL_SYMBOL}${getActiveFacetValue(v, 'urlName')}`).join(MbConstants.COLON_SYMBOL)

@Injectable({
  providedIn: 'root'
})
export class FilterProductsService {

  product$ = this.currentProductService.getProduct(ProductScope.VARIANTS).pipe(
    filter(isNotNullable),
    shareReplay(1)
  )
  productCode$ = this.product$.pipe(
    map(p => p.code),
    shareReplay(1)
  )
  variants$ = this.product$.pipe(
    map(p => p.variantOptions),
    shareReplay(1)
  )
  totalVariants$ = this.variants$.pipe(
    map(v => v?.length || 0),
    shareReplay(1)
  )
  isAntibody$ = this.product$.pipe(
    map(p => p.isAntibody || false),
    shareReplay(1)
  )
  facets$ = this.product$.pipe(
    map(p => toProductVariantFilterData(p.filters, p.variantOptions)),
    shareReplay(1)
  )
  totalFacets$ = this.facets$.pipe(
    map(f => f.findIndex(fd => fd?.code?.toLowerCase() === classificationCodes.sizeFilter?.toLowerCase()) !== -1),
    shareReplay(1)
  )
  urlFragments$ = this.activatedRoute.fragment.pipe(
    map(f => f?.split(MbConstants.COLON_SYMBOL)
      .map(v => v.split(MbConstants.EQUAL_SYMBOL))
      // mandatory lower case cuz of requirement though not always in links so force it
      .map(([k, v]) => ({ [k?.toLowerCase()]: v?.toLowerCase() }))
      .reduce((p, n) => ({ ...p, ...n })) || {}
    ),
    shareReplay(1)
  )
  appliedFacets$ = this.urlFragments$.pipe(
    switchMap(filters => this.facets$.pipe(
      map(facets => facets.map(facet => {
          const { urlName, filterOptions, prov } = facet
          const filteredValue = prov && filterOptions.find(fo =>
            Object.keys(filters).includes(fo.urlName))?.urlName || (urlName && filters[urlName])
          if (filteredValue) {
            filterOptions.forEach(fo => {
              fo.active = fo.urlName === filteredValue
            })
          } else {
            filterOptions.forEach(fo => {
              fo.active = false
            })
          }
          return facet
        })
      )
    )),
    shareReplay(1)
  )
  isDirty$ = this.urlFragments$.pipe(
    // cuz we need to be on same time with facets value changes
    switchMap(() => this.product$.pipe(
      map(p => p.urlEncodedName),
      map(urlName => !(urlName && this.router.url.indexOf(urlName) >= 0) || false),
    )),
    shareReplay(1)
  )
  appliedPrimaryFacet$ = this.appliedFacets$.pipe(
    map(af => af.filter(f => f.isPrimaryFilter)),
    map(toFacetRecord),
    shareReplay(1)
  )
  appliedNonPrimaryFacets$ = this.appliedFacets$.pipe(
    map(af => af.filter(f => !f.isPrimaryFilter)),
    map(toFacetRecord),
    shareReplay(1)
  )
  appliedSizeFacet$ = this.appliedNonPrimaryFacets$.pipe(
    map(v => ({
      [`${classificationCodes.sizeFilter}`]: v[classificationCodes.sizeFilter]
    })),
    shareReplay(1)
  )
  primaryVariants$ = this.appliedPrimaryFacet$.pipe(
    switchMap(af => this.variants$.pipe(
      map(vo => hasAppliedFacet(af) && vo?.filter(v =>
        v.variantOptionQualifiers?.find(voq => voq.qualifier && (af[voq.qualifier] === voq.value))) || vo)
    )),
    shareReplay(1)
  )
  filteredFacets$ = this.primaryVariants$.pipe(
    switchMap(pvs => this.appliedFacets$.pipe(
      map(af => af.map(f => ({
        ...f,
        filterOptions: f.isPrimaryFilter && f.filterOptions || f.filterOptions.filter(fo =>
          pvs?.map(pv => pv.variantOptionQualifiers).find(voqs =>
            voqs?.find(voq => voq.value === fo.code)))
      }))),
      // remove filters if no options
      map(af => af.filter(f => f.filterOptions.length)),
    )),
    shareReplay(1)
  )
  filteredVariants$ = this.appliedNonPrimaryFacets$.pipe(
    switchMap(af => this.primaryVariants$.pipe(
      map(vo => hasAppliedFacet(af) && vo?.filter(v =>
        v.variantOptionQualifiers?.find(voq => voq.qualifier && af[voq.qualifier] === voq.value)) || vo)
    )),
    shareReplay(1)
  )
  otherSizesVariants$ = this.appliedSizeFacet$.pipe(
    switchMap(af => this.primaryVariants$.pipe(
      map(vo => hasAppliedFacet(af) && vo?.filter(v =>
        !v.variantOptionQualifiers?.find(voq => voq.qualifier && af[voq.qualifier] === voq.value)) || [])
    )),
    shareReplay(1)
  )

  constructor (protected currentProductService: CurrentProductService,
               protected router: Router,
               protected activatedRoute: ActivatedRoute,
               protected windowRef: WindowRef) {
  }

  async applyFilter (filterSelection: FilterOptionSelection) {
    this.appliedFacets$.pipe(
      take(1),
      // reset filters if a new primary facet is selected
      map(facets => filterSelection.isPrimaryFilter && facets.filter(f => f.isPrimaryFilter) || facets),
      // set new values
      map(facets => facets.map(facet => {
        if (facet.code === filterSelection.filterCode) {
          facet.filterOptions.forEach(fo => {
            if (fo.code === filterSelection.valueCode) {
              fo.active = filterSelection.active
            } else {
              fo.active = false
            }
          })
        }
        return facet
      })),
      // construct url fragment
      map(facets => toHashUrl(facets)),
    ).subscribe(fragment => this.updateUrl(fragment))
  }

  protected updateUrl (fragment?: string) {
    return fragment && this.windowRef.isBrowser() && this.router.navigate([], {
      fragment,
      replaceUrl: true,
    }) || of(false).toPromise()
  }

  clearAllData () {
    //do we need to do something here?
  }
}
